import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock } from '../ui';
import SmartOrderComposer from '../components/SmartOrderComposer';

const copy = PHARMACY_COPY.ORDERS;
const EMPTY_FORM = { supplier_id: '', expected_date: '', notes: '', items: [] };

export default function PharmacyOrderCreatePage() {
  const navigate = useNavigate();
  const { toast, centralSite } = usePharmacy();
  const [suppliers, setSuppliers] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [supRes, medRes] = await Promise.all([
          pharmacyService.listSuppliers({ is_active: 'true' }),
          pharmacyService.listMedications({ status: 'ACTIVE' })
        ]);
        if (cancelled) return;
        setSuppliers(supRes.success ? supRes.data || [] : []);
        setMedications(medRes.success ? medRes.data || [] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lineCount = useMemo(
    () => form.items.filter((l) => l.medication_id && l.quantity_ordered).length,
    [form.items]
  );
  const canSave = lineCount > 0 && !busy;

  const goBack = () => navigate(CENTRAL_PHARMACY_PATHS.ORDERS);

  const submit = async (status) => {
    const items = form.items
      .filter((l) => l.medication_id && l.quantity_ordered)
      .map((l) => ({
        medication_id: l.medication_id,
        quantity_ordered: l.quantity_ordered,
        unit_cost: 0
      }))
      // UI = plus récent en haut ; insertion DB chronologique pour garder un id DESC correct
      .reverse();
    if (!items.length) {
      toast('warning', copy.LINES_HINT);
      return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.createOrder({
        supplier_id: form.supplier_id || null,
        expected_date: form.expected_date || null,
        notes: form.notes,
        status,
        items
      });
      if (res.success) {
        toast('success', copy.CREATE_OK.replace('{number}', res.data.order_number));
        navigate(CENTRAL_PHARMACY_PATHS.ORDER_DETAIL(res.data.id), { replace: true });
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cp-order-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.CREATE_TITLE}
        description={copy.CREATE_DESC}
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK_TO_LIST}
          </button>
        }
      />

      {loading ? (
        <LoadingBlock label="Préparation du formulaire…" />
      ) : (
        <>
          <SmartOrderComposer
            suppliers={suppliers}
            medications={medications}
            centralSiteId={centralSite?.id}
            value={form}
            onChange={setForm}
            copy={copy}
          />

          <div className="cp-order-page__actions">
            <div className="cp-order-page__actions-meta">
              <strong>{lineCount}</strong>
              <span>{copy.RESULTS_LINES}</span>
            </div>
            <div className="cp-order-page__actions-btns">
              <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack} disabled={busy}>
                {copy.CANCEL}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--ghost"
                onClick={() => submit('DRAFT')}
                disabled={!canSave}
              >
                {busy ? copy.SAVING : copy.DRAFT}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={() => submit('PENDING')}
                disabled={!canSave}
              >
                {busy ? copy.SAVING : copy.SUBMIT}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
